import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { UserProfile } from '@/types';
import { startOfMonth, endOfMonth, eachMonthOfInterval, subMonths, format } from 'date-fns';
import { ja } from 'date-fns/locale';

export interface MonthlyData {
  month: string;
  totalUsers: number;
  newUsers: number;
  freeUsers: number;
  proUsers: number;
}

export const useAdminChartData = () => {
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        // 過去6ヶ月のデータを取得
        const now = new Date();
        const sixMonthsAgo = subMonths(now, 5);
        const months = eachMonthOfInterval({ start: sixMonthsAgo, end: now });

        // 全ユーザーを取得
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        const users: UserProfile[] = usersSnapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as UserProfile[];

        const data: MonthlyData[] = [];

        for (const month of months) {
          const monthStart = startOfMonth(month);
          const monthEnd = endOfMonth(month);

          // その月までに登録されたユーザー数
          const usersUntilMonth = users.filter((user) => {
            const createdAt = user.createdAt?.toDate();
            return createdAt && createdAt <= monthEnd;
          });

          // その月に新規登録されたユーザー数
          const newUsers = users.filter((user) => {
            const createdAt = user.createdAt?.toDate();
            return createdAt && createdAt >= monthStart && createdAt <= monthEnd;
          });

          // プラン別集計（その月時点）
          const freeUsers = usersUntilMonth.filter((user) => user.plan === 'Free').length;
          const proUsers = usersUntilMonth.filter((user) => user.plan === 'Standard').length;

          data.push({
            month: format(month, 'yyyy/MM', { locale: ja }),
            totalUsers: usersUntilMonth.length,
            newUsers: newUsers.length,
            freeUsers,
            proUsers,
          });
        }

        setMonthlyData(data);
      } catch (error) {
        console.error('Failed to fetch chart data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  return { monthlyData, loading };
};
